import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import api from "utils/api";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatStatus = (status, isActive) => {
  if (!status) return "-";
  const normalized = status.replace(/_/g, " ");
  return isActive ? `${normalized} (Active)` : normalized;
};

function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await api.get("/student");
        if (!isMounted) return;

        const payload = Array.isArray(response?.data?.data) ? response.data.data : [];
        setStudents(payload);
        setError("");
      } catch (fetchError) {
        if (!isMounted) return;
        const message =
          fetchError?.response?.data?.message || fetchError?.message || "Failed to load students";
        setError(message);
        setStudents([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStudents();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredStudents = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return students;

    return students.filter((student) => {
      const bucket = [
        student.name,
        student.email,
        student.parentId?.fullName,
        student.parentId?.phone,
        student.class?.name,
        student.board?.name,
      ];

      return bucket.some((value) => value && String(value).toLowerCase().includes(trimmed));
    });
  }, [students, searchQuery]);

  const columns = useMemo(
    () => [
      { name: "serial", align: "center", width: "6%" },
      { name: "student", align: "left" },
      { name: "parent", align: "left" },
      { name: "contact", align: "left" },
      { name: "className", align: "center" },
      { name: "boardName", align: "center" },
      { name: "subscription", align: "center" },
      { name: "createdAt", align: "center", width: "16%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      filteredStudents.map((student, index) => ({
        hasBorder: true,
        serial: index + 1,
        student: student.name || "-",
        parent: student.parentId?.fullName || "-",
        contact: student.parentId?.phone || student.phone || "-",
        className: student.class?.name || "-",
        boardName: student.board?.name || "-",
        subscription: formatStatus(student.subscriptionStatus, student.hasActiveSubscription),
        createdAt: formatDate(student.createdAt),
      })),
    [filteredStudents]
  );

  const body = () => {
    if (loading) {
      return (
        <ArgonBox py={6} display="flex" justifyContent="center">
          <CircularProgress size={40} />
        </ArgonBox>
      );
    }

    if (error) {
      return (
        <ArgonBox py={2} px={2}>
          <Alert severity="error">{error}</Alert>
        </ArgonBox>
      );
    }

    if (rows.length === 0) {
      return (
        <ArgonBox py={6} textAlign="center">
          <ArgonTypography variant="button" color="secondary">
            No students found.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    return <Table columns={columns} rows={rows} />;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <ArgonBox
                px={3}
                py={3}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                gap={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <ArgonTypography variant="h5" fontWeight="medium">
                  Students
                </ArgonTypography>
                <ArgonInput
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by student, parent, class, or board"
                  size="large"
                  sx={{ minWidth: { xs: "100%", md: "260px" } }}
                />
              </ArgonBox>
              <ArgonBox px={3} pb={3}>
                {body()}
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Students;
